<template>
  <Transition name="mascot-loader-fade">
    <div v-if="visible" class="mascot-loader-screen" :class="{ 'mascot-loader-screen--contained': contained }" :aria-busy="active">
      <div
        class="mascot-loader"
        role="progressbar"
        :aria-label="label"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="progress"
        :data-mascot="mascot"
        :style="{ '--loader-art': `var(--loader-${mascot})`, '--loader-progress': `${progress}%` }"
      >
        <div class="mascot-loader__art" aria-hidden="true">
          <div class="mascot-loader__layer mascot-loader__layer--gray" />
          <div class="mascot-loader__layer mascot-loader__layer--color" />
        </div>
        <span class="mascot-loader__percent" aria-hidden="true">{{ progress }}%</span>
        <p class="mascot-loader__label">{{ label }}</p>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import '@/styles/mascot-loader.css'

const props = defineProps<{ active: boolean; label: string; contained?: boolean }>()
const characters = ['iskra', 'neo', 'maya', 'orion', 'vesta'] as const
const mascot = ref<(typeof characters)[number]>('iskra')
const progress = ref(0)
const visible = ref(false)
let timer: ReturnType<typeof setInterval> | undefined
let dismissal: ReturnType<typeof setTimeout> | undefined

function clearTimers() {
  clearInterval(timer)
  clearTimeout(dismissal)
}

watch(() => props.active, active => {
  clearTimers()
  if (active) {
    mascot.value = characters[Math.floor(Math.random() * characters.length)]!
    progress.value = 0
    visible.value = true
    const started = Date.now()
    timer = setInterval(() => {
      // Estimated readiness, not downloaded bytes. Never claim completion early.
      progress.value = Math.min(90, Math.floor(90 * (1 - Math.exp(-(Date.now() - started) / 2400))))
    }, 120)
  } else if (visible.value) {
    progress.value = 100
    dismissal = setTimeout(() => { visible.value = false }, 240)
  }
}, { immediate: true })

onUnmounted(clearTimers)
</script>

<style scoped>
.mascot-loader-screen--contained { position: absolute; z-index: 100; display: block; box-sizing: border-box; padding-block: clamp(24px, 18vh, 160px); }
.mascot-loader-screen--contained .mascot-loader { position: sticky; top: max(100px, calc(40vh - 100px)); margin-inline: auto; }
.mascot-loader-fade-leave-active { transition: opacity 160ms ease; }
.mascot-loader-fade-leave-to { opacity: 0; pointer-events: none; }
@media (prefers-reduced-motion: reduce) {
  .mascot-loader-fade-leave-active { transition: none; }
}
</style>
